import React from 'react';
import { FaUndo, FaPlus, FaMinus, FaClock, FaSync, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';

const formatIndianNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0.00';
    const sign = Number(num) < 0 ? '-' : '';
    const n = Math.abs(Number(num) || 0).toFixed(2);
    let [intPart, decPart] = n.split('.');
    if (intPart.length > 3) {
        const last3 = intPart.slice(-3);
        let rest = intPart.slice(0, -3);
        rest = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
        intPart = `${rest},${last3}`;
    }
    return `${sign}${intPart}.${decPart}`;
};

const formatBillTime = (dateVal) => {
    if (!dateVal) return '—';
    const d = new Date(dateVal);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    });
};

const TIME_SLOTS = (() => {
    const slots = [];
    for (let hour = 6; hour <= 23; hour++) {
        slots.push(`${String(hour).padStart(2, '0')}:00`);
        if (hour < 23) slots.push(`${String(hour).padStart(2, '0')}:30`);
    }
    return slots;
})();

/**
 * Walk-in bill returns workspace for Merchant Billing.
 * List + pagination on the page; item selection happens in a modal.
 */
const MerchantBillingReturnsPanel = ({
    returnSearch,
    setReturnSearch,
    returnLookupLoading,
    handleReturnLookup,
    returnBills,
    selectedReturnBill,
    resetReturnSelection,
    returnQtyByIndex,
    setReturnQtyByIndex,
    returnReason,
    setReturnReason,
    returnNote,
    setReturnNote,
    returnSelectionTotal,
    handleConfirmReturn,
    returnSubmitting,
    recentBills,
    recentBillsLoading,
    recentBillsLookbackLabel,
    recentBillsPagination,
    onRefreshRecentBills,
    onRecentBillsPageChange,
    returnStartTime,
    setReturnStartTime,
    returnEndTime,
    setReturnEndTime,
    onApplyReturnTimeFilter,
    onClearReturnTimeFilter,
    showTodayTimeFilter,
}) => {
    const showingSearchHits = returnBills && returnBills.length > 0;
    const listSource = showingSearchHits ? returnBills : (recentBills || []);
    const pagination = recentBillsPagination || {};
    const currentPage = pagination.currentPage || 1;
    const totalPages = pagination.totalPages || 1;
    const totalCount = pagination.totalCount ?? listSource.length;
    const showPagination = !showingSearchHits && totalPages > 1;

    const closeReturnModal = () => {
        if (returnSubmitting) return;
        resetReturnSelection(null);
    };

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-lg p-5">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800 mb-0.5 flex items-center gap-2">
                            <FaUndo className="text-rose-600 text-sm" /> Return Items
                        </h2>
                        <p className="text-xs text-slate-500">
                            Pick a bill to open return details, or search invoice / mobile.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onRefreshRecentBills}
                        disabled={recentBillsLoading}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                    >
                        <FaSync className={`text-[10px] ${recentBillsLoading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>

                <form onSubmit={handleReturnLookup} className="flex flex-col sm:flex-row gap-2 mb-3">
                    <input
                        type="text"
                        value={returnSearch}
                        onChange={(e) => setReturnSearch(e.target.value)}
                        placeholder="Invoice number or mobile…"
                        className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-rose-400"
                    />
                    <button
                        type="submit"
                        disabled={returnLookupLoading}
                        className="px-4 py-2.5 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 disabled:opacity-50"
                    >
                        {returnLookupLoading ? 'Searching…' : 'Find bill'}
                    </button>
                </form>

                {showTodayTimeFilter && (
                    <div className="mb-3 p-2.5 rounded-xl bg-rose-50/80 border border-rose-100">
                        <div className="flex items-center gap-2 text-[11px] font-semibold text-rose-800 mb-2">
                            <FaClock className="text-[10px]" /> Filter today by time
                        </div>
                        <div className="flex flex-wrap items-end gap-2">
                            <div>
                                <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">From</label>
                                <select
                                    value={returnStartTime || ''}
                                    onChange={(e) => setReturnStartTime(e.target.value)}
                                    className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm bg-white"
                                >
                                    <option value="">Start</option>
                                    {TIME_SLOTS.map((t) => (
                                        <option key={`s-${t}`} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">To</label>
                                <select
                                    value={returnEndTime || ''}
                                    onChange={(e) => setReturnEndTime(e.target.value)}
                                    className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm bg-white"
                                >
                                    <option value="">End</option>
                                    {TIME_SLOTS.map((t) => (
                                        <option key={`e-${t}`} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>
                            <button
                                type="button"
                                onClick={onApplyReturnTimeFilter}
                                disabled={!returnStartTime || !returnEndTime || recentBillsLoading}
                                className="px-3 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-semibold disabled:opacity-50"
                            >
                                Apply
                            </button>
                            {(returnStartTime || returnEndTime) && (
                                <button
                                    type="button"
                                    onClick={onClearReturnTimeFilter}
                                    className="px-3 py-1.5 rounded-lg bg-slate-200 text-slate-700 text-xs font-semibold"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                        {showingSearchHits
                            ? `Search results (${listSource.length})`
                            : `${recentBillsLookbackLabel || 'Recent bills'} (${totalCount})`}
                    </p>
                    {showingSearchHits && (
                        <button
                            type="button"
                            className="text-xs font-semibold text-rose-600 hover:underline"
                            onClick={() => {
                                setReturnSearch('');
                                onRefreshRecentBills();
                            }}
                        >
                            Clear search · show list
                        </button>
                    )}
                </div>

                <div className="overflow-hidden rounded-xl border border-slate-200">
                    {recentBillsLoading && listSource.length === 0 ? (
                        <div className="py-12 text-center text-slate-500 text-sm">Loading bills…</div>
                    ) : listSource.length === 0 ? (
                        <div className="py-12 text-center text-slate-500 text-sm px-4">
                            No walk-in bills in this window. Try search or widen the admin lookback on Customer Analytics.
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {listSource.map((bill) => {
                                const returnable = Number(bill.totalReturnableQuantity) || 0;
                                return (
                                    <button
                                        key={String(bill._id)}
                                        type="button"
                                        onClick={() => resetReturnSelection(bill)}
                                        className="w-full text-left px-3.5 py-3 hover:bg-rose-50/60 transition-colors"
                                    >
                                        <div className="flex flex-wrap items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <p className="font-semibold text-slate-800 text-sm truncate">
                                                    {bill.walkInOrderId || bill.invoiceNumber}
                                                </p>
                                                <p className="text-xs text-slate-500 mt-0.5">
                                                    {bill.customer?.customerPhone || bill.customer?.customerName || 'Walk-in'}
                                                    {' · '}
                                                    {bill.paymentInfo?.method || '—'}
                                                    {' · '}
                                                    {returnable > 0 ? `${returnable} returnable` : 'Fully returned'}
                                                </p>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <p className="text-sm font-bold text-slate-800">
                                                    ₹{formatIndianNumber(bill.financial?.netFinalAmount ?? bill.financial?.finalAmount)}
                                                </p>
                                                <p className="text-[11px] text-slate-500 flex items-center justify-end gap-1 mt-0.5">
                                                    <FaClock className="text-[10px]" />
                                                    {formatBillTime(bill.orderPlacedOn)}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {showPagination && (
                    <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                        <div className="text-sm text-gray-600">
                            Showing page {currentPage} of {totalPages} ({totalCount} bills)
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => onRecentBillsPageChange?.(currentPage - 1)}
                                disabled={!pagination.hasPrevPage || recentBillsLoading || currentPage <= 1}
                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
                            >
                                <FaChevronLeft className="text-[10px]" />
                                Previous
                            </button>
                            <span className="min-w-[4.5rem] text-center text-sm font-semibold text-slate-700 tabular-nums">
                                {currentPage} / {totalPages}
                            </span>
                            <button
                                type="button"
                                onClick={() => onRecentBillsPageChange?.(currentPage + 1)}
                                disabled={!pagination.hasNextPage || recentBillsLoading || currentPage >= totalPages}
                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
                            >
                                Next
                                <FaChevronRight className="text-[10px]" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {selectedReturnBill ? (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/45 backdrop-blur-[1px]"
                    onClick={closeReturnModal}
                    role="presentation"
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="return-bill-modal-title"
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100 shrink-0">
                            <div className="min-w-0">
                                <p id="return-bill-modal-title" className="text-base font-bold text-slate-800 truncate">
                                    {selectedReturnBill.walkInOrderId || selectedReturnBill.invoiceNumber}
                                </p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {selectedReturnBill.customer?.customerName || 'Walk-in'}
                                    {selectedReturnBill.customer?.customerPhone
                                        ? ` · ${selectedReturnBill.customer.customerPhone}`
                                        : ''}
                                    {' · '}
                                    {selectedReturnBill.paymentInfo?.method || '—'}
                                    {' · '}
                                    {formatBillTime(selectedReturnBill.orderPlacedOn)}
                                </p>
                            </div>
                            <div className="flex items-start gap-3 shrink-0">
                                <div className="text-right text-[11px] text-slate-500 hidden sm:block">
                                    <p>Original ₹{formatIndianNumber(selectedReturnBill.financial?.finalAmount)}</p>
                                    <p>Refunded ₹{formatIndianNumber(selectedReturnBill.financial?.refundedAmount || 0)}</p>
                                    <p className="font-semibold text-slate-800">
                                        Net ₹
                                        {formatIndianNumber(
                                            selectedReturnBill.financial?.netFinalAmount
                                                ?? selectedReturnBill.financial?.finalAmount
                                        )}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={closeReturnModal}
                                    disabled={returnSubmitting}
                                    className="h-8 w-8 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 flex items-center justify-center disabled:opacity-50"
                                    aria-label="Close"
                                >
                                    <IoClose size={18} />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                            <div className="space-y-2">
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                    Select items to return
                                </p>
                                {(selectedReturnBill.items || []).map((item) => {
                                    const maxQ = Number(item.returnableQuantity) || 0;
                                    const q = Math.max(0, Math.floor(Number(returnQtyByIndex[item.itemIndex]) || 0));
                                    return (
                                        <div
                                            key={`${item.itemIndex}-${item.productId}`}
                                            className={`flex items-center gap-3 p-3 rounded-xl border ${
                                                maxQ <= 0
                                                    ? 'opacity-50 border-slate-100 bg-slate-50'
                                                    : 'border-slate-200 bg-white'
                                            }`}
                                        >
                                            <div className="min-w-0 flex-1">
                                                <p className="font-semibold text-slate-800 text-sm line-clamp-1">
                                                    {item.productName}
                                                </p>
                                                <p className="text-xs text-slate-500">
                                                    {item.optionLabel}
                                                    {item.isFreeOfferItem
                                                        ? ' · Free gift'
                                                        : ` · ₹${formatIndianNumber(item.unitPrice)}`}
                                                    {' · '}
                                                    Returnable {maxQ}/{item.quantity}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <button
                                                    type="button"
                                                    disabled={maxQ <= 0 || q <= 0 || returnSubmitting}
                                                    onClick={() => setReturnQtyByIndex((prev) => ({
                                                        ...prev,
                                                        [item.itemIndex]: Math.max(0, q - 1),
                                                    }))}
                                                    className="h-8 w-8 rounded-lg bg-slate-100 disabled:opacity-40"
                                                >
                                                    <FaMinus className="mx-auto text-xs" />
                                                </button>
                                                <span className="w-8 text-center font-semibold tabular-nums">{q}</span>
                                                <button
                                                    type="button"
                                                    disabled={maxQ <= 0 || q >= maxQ || returnSubmitting}
                                                    onClick={() => setReturnQtyByIndex((prev) => ({
                                                        ...prev,
                                                        [item.itemIndex]: Math.min(maxQ, q + 1),
                                                    }))}
                                                    className="h-8 w-8 rounded-lg bg-slate-100 disabled:opacity-40"
                                                >
                                                    <FaPlus className="mx-auto text-xs" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                                {(selectedReturnBill.items || []).every((i) => !(i.returnableQuantity > 0)) && (
                                    <p className="text-sm text-slate-500 text-center py-4">
                                        All items on this bill have already been returned.
                                    </p>
                                )}
                            </div>

                            <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/60">
                                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                                    Return type *
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                                    <label
                                        className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer bg-white ${
                                            returnReason === 'just_return'
                                                ? 'border-emerald-400 ring-1 ring-emerald-200'
                                                : 'border-slate-200'
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="returnReason"
                                            checked={returnReason === 'just_return'}
                                            onChange={() => setReturnReason('just_return')}
                                            className="mt-1"
                                            disabled={returnSubmitting}
                                        />
                                        <span>
                                            <span className="block text-sm font-semibold text-slate-800">Just return</span>
                                            <span className="block text-[11px] text-slate-500">Restore sellable inventory</span>
                                        </span>
                                    </label>
                                    <label
                                        className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer bg-white ${
                                            returnReason === 'damaged'
                                                ? 'border-amber-400 ring-1 ring-amber-200'
                                                : 'border-slate-200'
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="returnReason"
                                            checked={returnReason === 'damaged'}
                                            onChange={() => setReturnReason('damaged')}
                                            className="mt-1"
                                            disabled={returnSubmitting}
                                        />
                                        <span>
                                            <span className="block text-sm font-semibold text-slate-800">Damaged/Expired item</span>
                                            <span className="block text-[11px] text-slate-500">Reverse sale · do not restock</span>
                                        </span>
                                    </label>
                                </div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1">Note (optional)</label>
                                <textarea
                                    value={returnNote}
                                    onChange={(e) => setReturnNote(e.target.value)}
                                    rows={2}
                                    disabled={returnSubmitting}
                                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
                                    placeholder="Optional note"
                                />
                            </div>
                        </div>

                        <div className="shrink-0 border-t border-slate-100 px-5 py-4 bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex items-center gap-4 text-sm">
                                <span className="text-slate-600">
                                    Units <span className="font-semibold text-slate-800">{returnSelectionTotal.qty}</span>
                                </span>
                                <span className="text-slate-600">
                                    Refund{' '}
                                    <span className="font-bold text-rose-700">
                                        ₹{formatIndianNumber(returnSelectionTotal.refund)}
                                    </span>
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={closeReturnModal}
                                    disabled={returnSubmitting}
                                    className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirmReturn}
                                    disabled={
                                        returnSubmitting
                                        || returnSelectionTotal.qty <= 0
                                        || !returnReason
                                    }
                                    className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-rose-500 to-rose-600 text-white text-sm font-bold disabled:opacity-50"
                                >
                                    {returnSubmitting ? 'Processing…' : 'Confirm return'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default MerchantBillingReturnsPanel;
